import {Organization} from "@/domains/-organization/models/Organization.ts";
import {Promotion} from "@/domains/-promotions/models/Promotion.ts";
import {WorkspacePerson} from "@/domains/-workspace/models/WorkspacePerson.ts";
import { dateFromServer } from "@/functions/dates";
import {
  CreditMode,
  GetWorkspace,
  GetWorkspaceCoachees,
  GetWorkspaceCredits,
  GetWorkspaceExterns,
  JourneyName,
  OrganizationId,
  PromotionId,
  sdk,
  WorkspaceId
} from "@/sdk.ts";
import {reactive} from "vue";

export interface CreditExpiration {
  date: Date;
  count: number;
}

export interface Credit {
  total: number;
  left: number;
  assigned: number;
  expiration: CreditExpiration[];
  options?: {
    name: JourneyName,
    credit: Credit
  }[]
}

export interface CreditFeta {
  level1: Credit
  level2?: Credit
}

export interface Credits {
  ai_research?: Credit;
  ascent_online?: Credit;
  ascent_vr?: Credit;
  bilan_vr?: Credit;
  bilan_vr_flash?: Credit;
  bilan_vr_quest?: Credit;
  bootcamp_online?: Credit;
  feta?: Credit;
  feta_ascent?: Credit;
  ignition_online?: Credit;
  ignition_vr?: Credit;
  leadership?: Credit;
  resilience?: Credit;
  tingari?: Credit;
}

export class Workspace {
  readonly id: WorkspaceId;
  readonly name: string;
  config: {
    allow_promotion: boolean;
  };
  allow_meeting_coach_selection: boolean;
  allow_automatic_email: boolean;

  // Externs
  public externs: WorkspacePerson[];
  public externsLoaded: boolean;
  private externsPromise: undefined | Promise<void>;

  // Coachees
  public coachees: WorkspacePerson[];
  public coacheesLoaded: boolean;
  private coacheesPromise: undefined | Promise<void>;
  public coacheesTotalItems: number;

  // Promotions
  public promotions: Promotion[];
  public promotionsLoaded: boolean;
  private promotionsPromise: undefined | Promise<void>;

  // Organizations
  public organizations: Organization[];
  public organizationsLoaded: boolean;
  private organizationsPromise: undefined | Promise<void>;

  // Credits
  //public credits: GetWorkspaceCredits.Result;
  public creditsLoaded: boolean;
  private creditsPromise: undefined | Promise<void>;
  public credit_mode: CreditMode | undefined;
  public credits: Credits;
  public creditsSubscription: CreditFeta | undefined;

  constructor(props: GetWorkspace.Result) {
    this.id = props.id;
    this.name = props.name;
    this.config = props.config;
    this.allow_meeting_coach_selection = props.allow_meeting_coach_selection;
    this.allow_automatic_email = props.allow_automatic_email;

    this.externs = [];
    this.externsPromise = undefined;
    this.externsLoaded = false;

    this.coachees = [];
    this.coacheesPromise = undefined;
    this.coacheesLoaded = false;
    this.coacheesTotalItems = 0;

    this.promotions = [];
    this.promotionsPromise = undefined;
    this.promotionsLoaded = false;

    this.organizations = [];
    this.organizationsPromise = undefined;
    this.organizationsLoaded = false;

    this.credits = {};
    this.creditsSubscription = undefined;
    this.creditsPromise = undefined;
    this.creditsLoaded = false;

  }

  public async fetchExterns(forceReload: boolean = true): Promise<void | undefined> {
    if (this.externsLoaded && !forceReload) return;
    if (this.externsPromise) return this.externsPromise;
    this.externsPromise = (async () => {
      try {
        const response = await sdk.getWorkspaceExterns(this.id);
        this.externs.splice(0, this.externs.length);
        this.externs.push(...response.data.map(row => new WorkspacePerson(this, {
          public_id: row.public_id,
          email: 'dummy-email',
          lang: 'en',
          is_verified: false,
          workspace_id: this.id,
          person_alias: row.person_alias,
          person_roles: row.person_roles,
        })));
        this.externsLoaded = true;
      } finally {
        this.externsPromise = undefined
      }
    })();
    return this.externsPromise;
  }

  public async fetchCoachees(query: GetWorkspaceCoachees.Query, signal?: AbortSignal, forceReload: boolean = true): Promise<void | undefined> {
    if (this.coacheesLoaded && !forceReload) return;
    if (this.coacheesPromise) return this.coacheesPromise;
    this.coacheesPromise = (async () => {
      try {
        const response = await sdk.getWorkspaceCoachees(this.id, query, {signal});
        this.coachees.splice(0, this.coachees.length);
        this.coachees.push(...response.data.data.map(row => new WorkspacePerson(this, row)));
        this.coacheesTotalItems = response.data.pagination.total_items;
        this.coacheesLoaded = true;
      } finally {
        this.coacheesPromise = undefined
      }
    })();
    return this.coacheesPromise;
  }

  public async fetchPromotions(forceReload: boolean = false): Promise<void | undefined> {
    if (this.promotionsLoaded && !forceReload) return;
    if (this.promotionsPromise) return this.promotionsPromise;
    this.promotionsPromise = (async () => {
      try {
        const response = await sdk.getWorkspacePromotions(this.id);
        this.promotions.splice(0, this.promotions.length);
        this.promotions.push(...response.data.map(row => new Promotion(row)));
        this.promotionsLoaded = true;
      } finally {
        this.promotionsPromise = undefined
      }
    })();
    return this.promotionsPromise;
  }

  public async fetchOrganizations(forceReload: boolean = true): Promise<void | undefined> {
    if (this.organizationsLoaded && !forceReload) return;
    if (this.organizationsPromise) return this.organizationsPromise;
    this.organizationsPromise = (async () => {
      try {
        const response = await sdk.getWorkspaceOrganizations(this.id);
        this.organizations.splice(0, this.organizations.length);
        this.organizations.push(...response.data.map(row => new Organization(row)));
        this.organizationsLoaded = true;
      } finally {
        this.organizationsPromise = undefined
      }
    })();
    return this.organizationsPromise;
  }

  public getOrganizationsById(organizationIds: OrganizationId[]): Organization[] {
    return this.organizations.filter(organization => organizationIds.includes(organization.pid));
  }
  public getPromotionsById(promotionIds: PromotionId[]): Promotion[] {
    return this.promotions.filter(promotion => promotionIds.includes(promotion.pid));
  }

  public async loadCredits(forceReload: boolean = false): Promise<void | undefined> {
    if (this.creditsLoaded && !forceReload) return
    if (this.creditsPromise) return this.creditsPromise;
    this.creditsPromise = (async () => {
      try {
        const response = await sdk.getWorkspace(this.id);
        //this.credits = response.data.credit;

        for(const [journeyName, credit] of Object.entries(response.data.credit)){
          
          if(journeyName === 'feta'){
            this.creditsSubscription = {
              level1: {
                left: credit.unexpired_credit === -1 ? -1 : credit.unexpired_credit - credit.assigned_credit,
                total: credit.unexpired_credit,
                assigned: credit.assigned_credit,
                expiration: []
              }
            }
            for(const [date, count] of Object.entries(credit.credit_expiration)){
              this.creditsSubscription.level1.expiration.push({
                date: dateFromServer(date),
                count: count as number
              })
            }
            if(Object.hasOwn(response.data.credit,'feta_ascent')){
              const creditLevel2 = response.data.credit['feta_ascent']!
              this.creditsSubscription.level2 = {
                left: creditLevel2.unexpired_credit === -1 ? -1 : creditLevel2.unexpired_credit - credit.assigned_credit,
                total: creditLevel2.unexpired_credit,
                assigned: creditLevel2.assigned_credit,
                expiration: []
                
              }
              for(const [date, count] of Object.entries(creditLevel2.credit_expiration)){
                this.creditsSubscription.level2.expiration.push({
                  date: dateFromServer(date),
                  count: count as number
                })
              }
            }
          }


          this.credits[journeyName] = {
            left: credit.unexpired_credit === -1 ? -1 : credit.unexpired_credit - credit.assigned_credit,
            total: credit.unexpired_credit,
            assigned: credit.assigned_credit,
            expiration: []
          }
          for(const [date, count] of Object.entries(credit.credit_expiration)){
            this.credits[journeyName].expiration.push({
              date: dateFromServer(date),
              count: count as number
            })
          }

          
        }

        console.log(this.credits)
        this.creditsLoaded = true;
        this.credit_mode = response.data.credit_mode
      } finally {
        this.creditsPromise = undefined
      }
    })();
    return this.creditsPromise;
  }

  get products(): JourneyName[] {
    return Object.keys(this.credits) as JourneyName[]
  }

  availableProducts(numberOfPersons: number) {
    return this.products
      .filter(productId => {
        return this.canStartJourney(productId, numberOfPersons)
      })
  }

  consumedCredits(name: JourneyName): number {
    return this.credits[name]?.assigned ?? 0
    /* return sum(
      this.coachees
        .map(wsp => {
          if (wsp.person.journeys.loaded) {
            return wsp.journeys.filter(journey => journey.name === name).length
          } else {
            return wsp.journeysLight.filter(journey => journey.name === name).length
          }
        })
    ) */
  }

  totalCredits(name: JourneyName): number {
    if(name === 'feta'){

    }
    if(this.credits[name]?.total !== -1)
      return (this.credits[name]?.total ?? 0)
    else
      return -1
  }

  totalAvailableCredits(): number {
    let total = 0
    let noLimitJourneyCount = 0
    for(const [creditName, credit] of Object.entries(this.credits)){
      if(creditName !== 'feta_ascent'){
        if(credit.total > -1)
          total += credit.left
        else
          noLimitJourneyCount++
      }
    }
    if(Object.keys(this.credits).filter(item => item !== 'feta_ascent').length > 0 && total === 0 && noLimitJourneyCount === Object.keys(this.credits).filter(item => item !== 'feta_ascent').length)
      return -1
    return total
  }

  remainingCredits(name: JourneyName): number {
    const total = this.totalCredits(name)
    if (total === -1 || total === 0) return total
    return total - this.consumedCredits(name)
  }

  canStartJourney(name: JourneyName, count: number) {
    if (!Object.hasOwn(this.credits, name)) return false

    const totalCredits = this.totalCredits(name)
    if (totalCredits === -1) return true

    const consumedCredits = this.consumedCredits(name)
    const remainingCredits = totalCredits - consumedCredits
    return count <= remainingCredits
  }
}